import { ReactNode, ComponentType, Component } from 'react';

import Nullable from 'packages/kakapo-ui-services/types/Nullable';

export interface IPromptableProps<V> {
	initialValues: V;
	onSubmit: (values: V) => void;
	onCancel: () => void;
}

export interface IWithPromptProps<V> {
	prompt: (initialValues: V, onSubmit: (values: V) => void) => void;
}

interface IPromptContainerState {
	promptChild: Nullable<ReactNode>;
}

/**
 * Higer order component to be used while "prompting" the user for a question or small
 * form. Eg. asking for a person's email. Every component that implements IPropmptableProps
 * can be used.
 *
 * @note Prompts are rendered directly underneath the component. A prompt is usually a modal
 * component
 *
 * @example `withPrompt(Register, FillCaptchaPromptContainer)`
 */
export const withPrompt = <P extends IWithPromptProps<V>, V>(
	ChildComponent: ComponentType<P>,
	PromptComponent: ComponentType<IPromptableProps<V>>,
) => {
	return class WithPrompt extends Component<Omit<P, 'prompt'>, IPromptContainerState> {
		constructor(props: P) {
			super(props);

			this.state = {
				promptChild: null,
			};
		}

		/**
		 * This function should be called within the wrapped component
		 *
		 * @abstract this function renders a prompting component which is put in
		 * state. Whenever the prompt is completed, this component is removed from
		 * the state and will disappear
		 */
		prompt = (initialValues: V, onSubmit: (values: V) => void) => {
			const promptChild = (
				<PromptComponent
					initialValues={initialValues}
					onSubmit={(values: V) => {
						onSubmit(values);

						// Remove the component from it's parent after it has finished.
						this.setState({ promptChild: null });
					}}
					onCancel={() => {
						this.setState({ promptChild: null });
					}}
				/>
			);

			this.setState({ promptChild });
		};

		render() {
			const { promptChild } = this.state;

			const UnsafeChildComponentTypeFix = ChildComponent as any;

			return (
				<>
					{/* eslint-disable-next-line react/jsx-props-no-spreading */}
					<UnsafeChildComponentTypeFix {...this.props} prompt={this.prompt} />
					{promptChild}
				</>
			);
		}
	};
};
